import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from '@app/component/translation/Translation';
import has from 'lodash/has';
import get from 'lodash/get';
import AsyncSelect from 'react-select/async';
import clsx from 'clsx';
import { Controller, RegisterOptions } from 'react-hook-form';
import {
	clearIndicatorStyles,
	controlStyles,
	dropdownIndicatorStyles,
	groupHeadingStyles,
	indicatorsContainerStyles,
	indicatorSeparatorStyles,
	loadingMessageStyles,
	menuPortalStyles,
	menuStyles,
	multiValueLabelStyles,
	multiValueRemoveStyles,
	multiValueStyles,
	noOptionsMessageStyles,
	optionStyles,
	placeholderStyles,
	selectInputStyles,
	singleValueStyles,
	valueContainerStyles,
} from '@app/component/form/input/autocomplete/AutocompleteStyles';
import { components } from 'react-select';
import { BuildingOffice2Icon } from '@heroicons/react/20/solid';
import FormInputValidator from '@app/component/form/input/FormInputValidator';
import SearchHotelResult from '@app/component/form/input/autocomplete/SearchHotelResult';
import AddNewHotelFromBookingUrlModal from '@app/component/form/input/autocomplete/AddNewHotelFromBookingUrlModal';
import Hotel from '@app/model/hotel/Hotel';
import HotelService from '@app/model/hotel/service/HotelService';
import SearchService from '@app/component/form/input/autocomplete/service/SearchService';
import Tag from '@app/component/tag/Tag';
import useModal from '@app/component/overlays/modal/ModalRef';

type Props = {
	form?: any,
	name?: string,
	placeholder: string,
	defaultValue?: SearchHotelResult,
	isRequired?: boolean,
	validation?: FormInputValidator,
	onChange?: (hotel: SearchHotelResult | undefined) => any,
	enforceError?: string,
	disabled?: boolean
}

const HotelAutocompleteField:
	React.FunctionComponent<PropsWithChildren<Props>> = ({
																												 children,
																												 name,
																												 placeholder,
																												 form,
																												 defaultValue,
																												 isRequired = false,
																												 validation,
																												 onChange,
																												 enforceError,
																												 disabled,
																											 }) => {

	const MIN_INPUT_LENGTH = 3;

	const { t } = useTranslation();
	const hasError = (!!form && !!name && has(form.formState.errors, name)) || !!enforceError;
	const errorMessage = hasError ? (!!enforceError ? t(enforceError) : (get(form.formState.errors, name + '.message', isRequired ? t('common.form.error.required') : ''))) : '';
	const modal = useModal();

	const [hotel, setHotel] = useState<SearchHotelResult>();

	const val = !!form && form.watch(name);

	useEffect(() => {
		if (!!val)
			HotelService.findHotelById(val).then(d => setHotel(mapHotel(d)));
		else setHotel(undefined);
	}, [val]);

	useEffect(() => {
		setHotel(defaultValue);
	}, [defaultValue]);

	useEffect(() => {
		if (!!onChange) onChange(hotel);
	}, [hotel]);

	const options: Partial<RegisterOptions> = {
		required: isRequired,
	};

	const mapHotel = (hotel: Hotel): SearchHotelResult => {
		return {
			address: hotel.location?.city,
			countryName: hotel.location?.country.name,
			coordinates: hotel.location?.coordinates,
			id: hotel.id,
			name: hotel.name,
		};
	};

	if (!!validation) {
		if (typeof validation === 'function') {
			options.validate = validation;
		} else if (typeof validation === 'object') {
			options.validate = {
				checkUrl: (value) => {
					return (validation as FormInputValidator).check(value);
				},
			};
		}
	}

	const onCreateHotel = (hotel: Hotel) => {
		form.setValue(name, hotel.id);
		if (!!onChange) onChange(mapHotel(hotel));
	};

	const formatOptionLabel = (hotel: SearchHotelResult) => (
		<div style={{ display: 'flex' }}>
			<div>{hotel.name}</div>
			<div style={{ marginLeft: '10px', color: '#ccc' }}>
				{(!!hotel.address ? hotel.address + ', ' : '') + hotel.countryName}
			</div>
		</div>
	);

	const ValueContainer: React.FunctionComponent<PropsWithChildren<any>> = ({ children, ...props }) => {
		const displayImport = !props.selectProps.isDisabled && !props.selectProps.value;
		const SelectValueContainer = components.ValueContainer as React.ComponentType<any>;

		return (
			<SelectValueContainer {...props}>
					{!!children && (
						<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
							<BuildingOffice2Icon className="h-5 w-5 text-slate-400" />
						</div>
					)}
					<div className="ml-8 flex">
						{children}
						{displayImport && <a onClick={() => modal.open()} className="hidden sm:block"><Tag state="info"
																																															 extraClassName="flex-right hover:drop-shadow-md">Can't
							find property?</Tag></a>}
					</div>
			</SelectValueContainer>
		);
	};

	const field = () => {
		return <AsyncSelect placeholder={placeholder}
												unstyled
												defaultValue={defaultValue}
												components={{ ValueContainer }}
			// isLoading={searchHotels.isLoading}
												getOptionValue={(o: SearchHotelResult) => '' + o.id}
												getOptionLabel={(o: SearchHotelResult) => o.name}
												formatOptionLabel={formatOptionLabel}
												loadOptions={SearchService.searchHotels}
												value={hotel}
												isClearable={true}
												menuPortalTarget={document.body}
												onChange={(v: SearchHotelResult) => {
													if (!!form) form.setValue(name, !!v ? v.id : undefined);
													else setHotel(v);

												}}
												isDisabled={disabled}
												styles={{
													input: (base) => ({
														...base,
														'input:focus': {
															boxShadow: 'none',
														},
													}),
												}}
												noOptionsMessage={
													(input) => input.inputValue.length >= MIN_INPUT_LENGTH
														? 'Hotel not found'
														: 'Search input must be at least 3 characters'
												}
												classNames={{
													control: ({ isFocused }) =>
														clsx(
															isFocused ? controlStyles(!!errorMessage, disabled).focus : controlStyles(!!errorMessage, disabled).nonFocus,
															controlStyles(!!errorMessage, disabled).base,
														),
													placeholder: () => placeholderStyles(!!errorMessage),
													input: () => selectInputStyles,
													valueContainer: () => valueContainerStyles,
													singleValue: () => singleValueStyles,
													multiValue: () => multiValueStyles,
													multiValueLabel: () => multiValueLabelStyles,
													multiValueRemove: () => multiValueRemoveStyles,
													indicatorsContainer: () => indicatorsContainerStyles,
													clearIndicator: () => clearIndicatorStyles,
													indicatorSeparator: () => indicatorSeparatorStyles,
													dropdownIndicator: () => dropdownIndicatorStyles,
													menu: () => menuStyles,
													groupHeading: () => groupHeadingStyles,
													option: ({ isFocused, isSelected }) =>
														clsx(
															isFocused && optionStyles.focus,
															isSelected && optionStyles.selected,
															optionStyles.base,
														),
													noOptionsMessage: () => noOptionsMessageStyles,
													loadingMessage: () => loadingMessageStyles,
													menuPortal: () => menuPortalStyles,
												}}
		/>;
	};


	return <div id="hotel-autocomplete-wrapper" className="flex-grow">
		<div>
			{!!form && !!name && <Controller name={name}
																			 control={form.control}
																			 rules={options}
																			 render={() => field()} />}
			{(!form || !name) && field()}

		</div>
		{!disabled &&
			<a className="font-semibold text-sm text-blue-600 hover:text-blue-500 cursor-pointer pl-2 block sm:hidden"
				 onClick={() => modal.open()}>Can't find property?</a>}
		{!!errorMessage && <p className="help help-error">{errorMessage}</p>}
		<AddNewHotelFromBookingUrlModal onCreate={onCreateHotel} modal={modal} />
	</div>;
};


export default HotelAutocompleteField;
